import type { UpdateClassesChange } from "@/store/zustand/changeLedgerTypes";
import {
  addClassCssRule,
  getElementsForCodebaseId,
  removeClassCssRule,
} from "./domUtils";
import { v4 as uuidv4 } from "uuid";
import type { NavTreeNode } from "@/utils/types";
import type { NavTreeUpdates } from "./navTreeLookupUtils";
import { ApplyInstantUpdateProps } from "./utils";

interface ApplyUpdateClassesProps extends ApplyInstantUpdateProps {
  throwawayUpdatesClasses: {
    [codebaseId: string]: {
      class: string;
      selectorId: string;
    }[];
  };
}

export const applyUpdateClassesChangeToDom = (
  props: ApplyUpdateClassesProps,
) => {
  const {
    iframeDocument,
    storyboardId,
    throwawayUpdatesClasses,
    persistentData,
  } = props;
  const change = props.change as UpdateClassesChange;

  const {
    codebaseIdToUpdateClass,
    newClasses,
    oldClasses,
    involvedClasses,
    isInstantChangeOnly,
  } = change.changeFields;

  const elements = getElementsForCodebaseId(
    iframeDocument,
    codebaseIdToUpdateClass,
  );

  if (!elements.length) {
    return;
  }

  persistentData.storyboardsAppliedTo.add(storyboardId);

  for (const oldClass of oldClasses) {
    if (!oldClass?.trim()) {
      continue;
    }

    elements.forEach((element) => {
      element.classList.remove(oldClass);
    });
  }

  for (const { class: className, selectorId } of throwawayUpdatesClasses[
    codebaseIdToUpdateClass
  ] || []) {
    removeClassCssRule(iframeDocument, selectorId);

    if (!className?.trim()) {
      return;
    }

    elements.forEach((element) => {
      element.classList.remove(className);
    });
  }

  for (const newClass of newClasses) {
    if (!newClass?.trim()) {
      continue;
    }

    elements.forEach((element) => {
      element.classList.add(newClass);
    });
  }

  throwawayUpdatesClasses[codebaseIdToUpdateClass] = [];

  for (const involvedClass of involvedClasses) {
    const selectorId = uuidv4();
    addClassCssRule(
      iframeDocument,
      involvedClass.tailwind,
      involvedClass.css,
      selectorId,
      involvedClass.variant,
    );

    if (isInstantChangeOnly) {
      const classToSet = involvedClass.variant
        ? `${involvedClass.variant}:${involvedClass.tailwind}`
        : involvedClass.tailwind;

      throwawayUpdatesClasses[codebaseIdToUpdateClass].push({
        class: classToSet,
        selectorId,
      });
    }
  }
};

export const undoUpdateClassesChangeToDom = (
  props: ApplyUpdateClassesProps,
) => {
  const { iframeDocument, throwawayUpdatesClasses } = props;
  const change = props.change as UpdateClassesChange;

  const { codebaseIdToUpdateClass, newClasses, oldClasses } =
    change.changeFields;

  for (const newClass of newClasses) {
    if (!newClass?.trim()) {
      continue;
    }

    getElementsForCodebaseId(iframeDocument, codebaseIdToUpdateClass).forEach(
      (element) => {
        element.classList.remove(newClass);
      },
    );
  }

  for (const oldClass of oldClasses) {
    if (!oldClass?.trim()) {
      continue;
    }

    getElementsForCodebaseId(iframeDocument, codebaseIdToUpdateClass).forEach(
      (element) => {
        element.classList.add(oldClass);
      },
    );
  }

  for (const { class: className, selectorId } of throwawayUpdatesClasses[
    codebaseIdToUpdateClass
  ] || []) {
    removeClassCssRule(iframeDocument, selectorId);
  }

  throwawayUpdatesClasses[codebaseIdToUpdateClass] = [];
};

// Helper function to find a nav tree node by codebase ID
const findNavTreeNodeByCodebaseId = (
  node: NavTreeNode | null,
  codebaseId: string,
): NavTreeNode | null => {
  if (!node) return null;

  if (node.tempoElement?.codebaseId === codebaseId) {
    return node;
  }

  for (const child of node.children || []) {
    const found = findNavTreeNodeByCodebaseId(child, codebaseId);
    if (found) return found;
  }

  return null;
};

// Update nav tree for UPDATE_CLASSES change - creates new node objects for React re-rendering
export const applyUpdateClassesChangeToNavTree = (
  props: ApplyUpdateClassesProps,
): void => {
  const { navTreeUpdates } = props;

  const change = props.change as UpdateClassesChange;

  const codebaseId = change.changeFields.codebaseIdToUpdateClass;
  const nodesToUpdate = navTreeUpdates.codebaseIdLookup[codebaseId] || [];

  const classesToRemove = new Set(change.changeFields.oldClasses || []);
  const classesToAdd = new Set(change.changeFields.newClasses || []);

  // Replace each node with a new node object
  nodesToUpdate.forEach((oldNode) => {
    oldNode.classList = Array.from(oldNode.classList || []).filter(
      (className) => !classesToRemove.has(className),
    );

    classesToAdd.forEach((className) => {
      if (!oldNode.classList?.includes?.(className)) {
        oldNode.classList?.push?.(className);
      }
    });
  });
};

// Undo nav tree update for UPDATE_CLASSES change - creates new node objects for React re-rendering
export const undoUpdateClassesChangeToNavTree = (
  props: ApplyUpdateClassesProps,
): void => {
  const { navTreeUpdates } = props;
  const change = props.change as UpdateClassesChange;

  const codebaseId = change.changeFields.codebaseIdToUpdateClass;
  const nodesToUpdate = navTreeUpdates.codebaseIdLookup[codebaseId] || [];

  const classesToAdd = new Set(change.changeFields.oldClasses || []);
  const classesToRemove = new Set(change.changeFields.newClasses || []);

  // Replace each node with a new node object
  nodesToUpdate.forEach((oldNode) => {
    oldNode.classList = Array.from(oldNode.classList || []).filter(
      (className) => !classesToRemove.has(className),
    );

    classesToAdd.forEach((className) => {
      if (!oldNode.classList?.includes?.(className)) {
        oldNode.classList?.push?.(className);
      }
    });
  });
};

// Consolidated apply function that handles both DOM and nav tree
export const applyUpdateClassesChange = (
  props: ApplyUpdateClassesProps,
): void => {
  // Apply DOM changes
  applyUpdateClassesChangeToDom(props);

  // Apply nav tree changes
  if (!props.skipUpdateNavTree) {
    applyUpdateClassesChangeToNavTree(props);
  }
};

// Consolidated undo function that handles both DOM and nav tree
export const undoUpdateClassesChange = (
  props: ApplyUpdateClassesProps,
): void => {
  // Undo DOM changes
  undoUpdateClassesChangeToDom(props);

  // Undo nav tree changes
  if (!props.skipUpdateNavTree) {
    undoUpdateClassesChangeToNavTree(props);
  }
};
